import { useState, useEffect } from "react";
import { Discipline, Startgroup } from "./api/Dtos";
import { useGlobalContext } from "./Layout/GlobalState";
import {
  Button,
  Select,
  VStack,
  Text,
  HStack,
  Box,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  IconButton,
  Tfoot,
  Spacer,
  Tooltip,
} from "@chakra-ui/react";
import { timeInMsToString } from "./Services/ResultService";
import Startgroups from "./Startgroups";
import { MultiSelect } from "./Reports/MultiSelect";
import {
  DisciplineStartgroupMapping,
  useDisciplineStartgroupMappings,
} from "./Services/StartgroupService";
import { updateGlobalContextCompetitionWithDisciplineAndStartgroups } from "./Services/CompetitionService";
import { Pencil } from "lucide-react";
import { PromptForConfirmationModal } from "./Modals/PromptForConfirmationModal";
import { PUT } from "./api/BackendApi";
import { toast } from "react-toastify";

export function DisciplineStartgroupMap() {
  const globalContext = useGlobalContext();
  const { persistedMappings, saveMapping } = useDisciplineStartgroupMappings();

  const [startgroupIdToEdit, setStartgroupIdToEdit] = useState<number | null>(
    null
  );

  const [pendingChanges, setPendingChanges] = useState<{
    startgroup: Startgroup;
    disciplines: Array<Discipline>;
  } | null>(null);

  // Fetch all disciplines and startgroups on loading this component
  useEffect(() => {
    if (globalContext.competition?.id) {
      updateGlobalContextCompetitionWithDisciplineAndStartgroups(
        globalContext.competition.id,
        globalContext
      );
    }
  }, []);

  return (
    <div>
      <VStack
        padding={"10px"}
        spacing={20}
        justify={"space-evenly"}
        marginBottom={"30px"}
      >
        <CreateNewMapping
          startgroups={globalContext.competition?.startgroups ?? []}
          disciplines={globalContext.competition?.disciplines ?? []}
          selectionPrompt={
            startgroupIdToEdit == null
              ? null
              : persistedMappings.filter(
                  (m) => m.startgroup.id == startgroupIdToEdit
                )
          }
          onSave={async (
            startgroup: Startgroup,
            disciplines: Array<Discipline>
          ) => {
            setPendingChanges({
              disciplines: disciplines,
              startgroup: startgroup,
            });
          }}
          onSelectStartgroup={(startgroupId: number) => {
            setStartgroupIdToEdit(startgroupId);
          }}
          onCancel={() => {
            setStartgroupIdToEdit(null);
          }}
        />
        {pendingChanges && (
          <PromptForConfirmationModal
            colorScheme="red"
            onConfirm={() => {
              saveMapping(
                pendingChanges.startgroup,
                pendingChanges.disciplines
              ).finally(() => {
                setPendingChanges(null);
                setStartgroupIdToEdit(null);
              });
            }}
            onClose={() => {
              setStartgroupIdToEdit(null);
              setPendingChanges(null);
            }}
            request={"Vill du spara?"}
          />
        )}
        <DisciplineMappingTable
          highlightStartgroupId={startgroupIdToEdit}
          mappings={persistedMappings}
          onEdit={(startgroupId) => setStartgroupIdToEdit(startgroupId)}
        />
        <Tooltip
          marginTop={"50px"}
          label={
            "Tilldela alla löpare startgrupper startgrupp unikt kan bestämmas från klassen de är anmälda i."
          }
        >
          <Button
            onClick={() => {
              if (globalContext.competition?.id) {
                PUT.participantAutoAssignStartgroups(
                  globalContext.competition?.id
                )
                  .then(() => {
                    toast.success("Tilldelningen av startgupper slutförd.");
                  })
                  .catch((e) => {
                    toast.error("Tilldelningen av startgrupper misslyckades.");
                  });
              }
            }}
            colorScheme="yellow"
          >
            Force startgroup assignment
          </Button>
        </Tooltip>
      </VStack>
    </div>
  );
}

export interface StartgroupWithDates {
  id: number;
  starttime_planned: Date | null;
  starttime_actual: Date | null;
  closetime_planned: Date | null;
  closetime_actual: Date | null;
}

function CreateNewMapping(props: {
  startgroups: Array<Startgroup>;
  disciplines: Array<Discipline>;
  selectionPrompt: Array<DisciplineStartgroupMapping> | null;
  onSave: (
    startgroup: Startgroup,
    disciplines: Array<Discipline>
  ) => Promise<void>;
  onCancel: () => void;
  onSelectStartgroup: (startgroupId: number) => void;
}) {
  const [selectedStartgroup, setSelectedStartgroup] = useState<
    Startgroup | undefined
  >(undefined);
  const [selectedDisciplines, setSelectedDisciplines] = useState<
    Array<Discipline>
  >([]);

  useEffect(() => {
    if (props.selectionPrompt != null && props.selectionPrompt.length > 0) {
      setSelectedStartgroup(props.selectionPrompt[0].startgroup);
      setSelectedDisciplines([]);
    }
  }, [props.selectionPrompt]);

  return (
    <VStack spacing={8} width={"100%"}>
      <VStack
        spacing={8}
        minW={"800px"}
        padding="60px"
        borderColor={"black"}
        borderWidth={"2px"}
        borderRadius={"10px"}
      >
        <HStack width={"100%"}>
          <Text>Startgrupp: </Text>
          <Select
            marginRight={"40px"}
            minW={100}
            maxW={250}
            placeholder={"-"}
            value={selectedStartgroup?.id}
            onChange={(event) => {
              const id = parseInt(event.target?.value);
              setSelectedStartgroup(
                (props.startgroups.filter((s) => s.id === id) || [undefined])[0]
              );
              props.onSelectStartgroup(id);
            }}
          >
            {props.startgroups.map((s) => {
              return (
                <option
                  key={s.id}
                  value={s.id}
                  selected={s.id === selectedStartgroup?.id}
                >
                  {timeInMsToString(s.starttime_planned)}
                </option>
              );
            })}
          </Select>
          {selectedStartgroup && (
            <HStack>
              <Text>Klass: </Text>
              <MultiSelect
                direction="row"
                hideTitle
                hideSplitCheckbox
                title="Välj klass(er)"
                values={props.disciplines.map((d) => d.id) ?? []}
                displayValues={props.disciplines.map((d) => d.name) ?? []}
                onChange={(selection: Array<string | number>) => {
                  setSelectedDisciplines(
                    props.disciplines.filter((d) => selection.includes(d.id)) ??
                      []
                  );
                }}
              />
            </HStack>
          )}
        </HStack>
        {selectedStartgroup != null && (
          <HStack>
            <Button
              marginRight={"30px"}
              onClick={() => {
                props
                  .onSave(selectedStartgroup, selectedDisciplines)
                  .then(() => {
                    setSelectedDisciplines([]);
                    setSelectedStartgroup(undefined);
                  });
              }}
              padding="40px"
              paddingTop={"10px"}
              paddingBlock={"10px"}
              minW={"200px"}
              colorScheme="green"
            >
              Spara
            </Button>
            <Button
              onClick={() => {
                props.onCancel();
                setSelectedDisciplines([]);
                setSelectedStartgroup(undefined);
              }}
              padding="30px"
              paddingTop={"10px"}
              paddingBlock={"10px"}
              minW={"150px"}
            >
              Avbryt
            </Button>
          </HStack>
        )}
      </VStack>
    </VStack>
  );
}

export function DisciplineMappingTable(props: {
  highlightStartgroupId: number | null;
  mappings: DisciplineStartgroupMapping[];
  onEdit: (startgroupId: number) => void;
}) {
  let startgroupIds = new Set(props.mappings.map((m) => m.startgroup.id));
  const displayData = [...startgroupIds].map((sid) => ({
    startgroup: props.mappings.find((m) => m.startgroup.id == sid)?.startgroup,
    disciplines: props.mappings
      .filter((m) => m.startgroup.id == sid)
      .map((m) => m.discipline),
  }));

  return (
    <Box overflowX="auto" width={"90%"}>
      <Table variant="simple" size="md">
        <Thead bg="gray.100">
          <Tr>
            <Th>Startgrupp ID</Th>
            <Th>Startgrupp</Th>
            <Th>Klasser</Th>
            <Th>Åtgärd</Th>
          </Tr>
        </Thead>
        <Tbody>
          {displayData.map((row) => (
            <Tr
              key={row.startgroup?.id}
              bgColor={
                props.highlightStartgroupId === row.startgroup?.id
                  ? "yellow.100"
                  : undefined
              }
            >
              <Td>{row.startgroup?.id}</Td>
              <Td>{timeInMsToString(row.startgroup?.starttime_planned)}</Td>
              <Td>{row.disciplines.map((d) => d.name).join(", ")}</Td>
              <Td>
                <IconButton
                  aria-label="Redigera"
                  icon={<Pencil size={16} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={() => {
                    if (row.startgroup?.id) {
                      props.onEdit(row.startgroup.id);
                    }
                  }}
                  size="sm"
                />
              </Td>
            </Tr>
          ))}
        </Tbody>
        <Tfoot bg="gray.100">
          <Tr>
            <Th>Startgrupp ID</Th>
            <Th>Startgrupp</Th>
            <Th>Klasser</Th>
            <Th>Åtgärd</Th>
          </Tr>
        </Tfoot>
      </Table>
    </Box>
  );
}

export default Startgroups;
